import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  FaBell,
  FaTimes,
  FaCheck,
  FaEye,
  FaCalendarAlt,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { crmFollowupApi } from '../../../api/crmApi';
import { TokenManager } from '../../../utils/tokenManager';
import './FollowupAlert.css';

const READ_IDS_PREFIX = 'crm_followup_read_';
const READ_IDS_MAX = 1000;

const readDailyKey = () =>
  `crm_followup_alert_read_${TokenManager.getUsername() || 'anon'}_${new Date().toISOString().slice(0, 10)}`;

const isMarkedRead = () => {
  try { return localStorage.getItem(readDailyKey()) === '1'; } catch { return false; }
};
const markDailyRead = () => {
  try { localStorage.setItem(readDailyKey(), '1'); } catch { /* ignore */ }
};

const getUserReadIds = () => {
  try {
    const raw = localStorage.getItem(READ_IDS_PREFIX + (TokenManager.getUsername() || 'anon'));
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
};
const storeUserReadIds = (ids) => {
  try {
    const list = Array.from(ids).slice(-READ_IDS_MAX);
    localStorage.setItem(READ_IDS_PREFIX + (TokenManager.getUsername() || 'anon'), JSON.stringify(list));
  } catch { /* ignore */ }
};

const timeOf = (d) =>
  d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';
const dateOf = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

export default function FollowupAlert() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [today, setToday] = useState([]);
  const [overdue, setOverdue] = useState([]);

  useEffect(() => {
    if (isMarkedRead()) return;
    let cancelled = false;
    const readIds = getUserReadIds();

    (async () => {
      try {
        const [t, o] = await Promise.allSettled([
          crmFollowupApi.getToday(),
          crmFollowupApi.getOverdue(),
        ]);
        if (cancelled) return;

        const todayList = (t.status === 'fulfilled' && Array.isArray(t.value?.data) ? t.value.data : [])
          .filter((f) => !readIds.has(String(f.id)));
        const overdueList = (o.status === 'fulfilled' && Array.isArray(o.value?.data) ? o.value.data : [])
          .filter((f) => !readIds.has(String(f.id)));

        if (!todayList.length && !overdueList.length) return;

        setToday(todayList);
        setOverdue(overdueList);
        setOpen(true);
      } catch {
        /* Silent — reminders are non-critical. */
      }
    })();

    return () => { cancelled = true; };
  }, []);

  if (!open) return null;

  const hasToday = today.length > 0;
  const hasOverdue = overdue.length > 0;
  const variant = hasToday && hasOverdue ? 'both' : hasOverdue ? 'overdue' : 'today';

  const close = () => setOpen(false);
  const dismissAll = () => {
    markDailyRead();
    const readIds = getUserReadIds();
    [...today, ...overdue].forEach((f) => { if (f.id != null) readIds.add(String(f.id)); });
    storeUserReadIds(readIds);
  };
  const handleMarkRead = () => { dismissAll(); close(); };
  const handleView = () => { dismissAll(); close(); navigate('/crm/followups'); };

  const title =
    variant === 'both' ? 'Follow-up Reminders'
      : variant === 'overdue' ? 'Overdue Follow-ups'
        : "Today's Follow-ups";

  const message =
    variant === 'both'
      ? `You have ${today.length} follow-up${today.length > 1 ? 's' : ''} scheduled for today and ${overdue.length} overdue follow-up${overdue.length > 1 ? 's' : ''} that need attention.`
      : variant === 'overdue'
        ? `You have ${overdue.length} overdue follow-up${overdue.length > 1 ? 's' : ''}. Today's follow-up is not scheduled, but these need immediate attention.`
        : `Today's follow-up is scheduled — you have ${today.length} follow-up${today.length > 1 ? 's' : ''} for today.`;

  const renderItem = (f, kind, index) => (
    <li key={`${kind}-${f.id ?? index}`} className="cfa-item">
      <div className="cfa-item-main">
        <div className="cfa-item-lead">{f.lead || f.leadName || 'Follow-up'}</div>
        {f.company && <div className="cfa-item-company">{f.company}</div>}
        {f.notes && <div className="cfa-item-notes">{f.notes}</div>}
      </div>
      <div className="cfa-item-meta">
        {f.type && <span className="cfa-chip cfa-chip-type">{f.type}</span>}
        <span className={`cfa-chip cfa-chip-time ${kind === 'overdue' ? 'is-overdue' : ''}`}>
          {kind === 'today' ? timeOf(f.scheduledAt) : dateOf(f.scheduledAt)}
        </span>
        {f.priority && (
          <span className={`cfa-chip cfa-priority cfa-priority-${String(f.priority).toLowerCase()}`}>
            {f.priority}
          </span>
        )}
      </div>
    </li>
  );

  return createPortal(
    <div className="cfa-backdrop" onClick={(e) => e.target === e.currentTarget && close()}>
      <div
        className={`cfa-modal cfa-modal-${variant}`}
        role="dialog"
        aria-modal="true"
        aria-label="Follow-up reminders"
      >
        <div className="cfa-header">
          <div className={`cfa-header-icon cfa-header-icon-${variant}`}>
            {variant === 'overdue' ? <FaExclamationTriangle aria-hidden="true" /> : <FaBell aria-hidden="true" />}
          </div>
          <div className="cfa-header-text">
            <h3>{title}</h3>
            <p>{message}</p>
          </div>
          <button type="button" className="cfa-close" onClick={close} aria-label="Close">
            <FaTimes aria-hidden="true" />
          </button>
        </div>

        <div className="cfa-body">
          {hasToday && (
            <section className="cfa-section cfa-section-today">
              <div className="cfa-section-head">
                <FaCalendarAlt aria-hidden="true" />
                <span>Today&apos;s Follow-ups</span>
                <span className="cfa-count">{today.length}</span>
              </div>
              <ul className="cfa-list">{today.map((f, i) => renderItem(f, 'today', i))}</ul>
            </section>
          )}

          {hasOverdue && (
            <section className="cfa-section cfa-section-overdue">
              <div className="cfa-section-head">
                <FaExclamationTriangle aria-hidden="true" />
                <span>Overdue Follow-ups</span>
                <span className="cfa-count">{overdue.length}</span>
              </div>
              <ul className="cfa-list">{overdue.map((f, i) => renderItem(f, 'overdue', i))}</ul>
            </section>
          )}
        </div>

        <div className="cfa-footer">
          <button type="button" className="cfa-btn cfa-btn-ghost" onClick={handleMarkRead}>
            <FaCheck aria-hidden="true" /> Mark as read
          </button>
          <button type="button" className="cfa-btn cfa-btn-primary" onClick={handleView}>
            <FaEye aria-hidden="true" /> View
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
